import React, { useState, useMemo } from "react";
import { useParams } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { CreateChargeAssetLinkageFields, ChargeWithLinkages } from "../../../api/charges";
import { selectAssetIdsForCharge, selectChargeById } from "../Logic/chargesSlice";

import AssetSelectionStep from "./AssetSelectionStep";
import ManagementModal from "../../../components/ui/modals/ManagementModal";
import ReviewChangesModal from "./ReviewChangesModal";
import { addAssetLinkages } from "../Logic/chargesThunk";
import { showErrorToast, showSuccessToast } from "../../../lib/toast";

interface AddExistingAssetModalProps {
  chargeId: string;
  onBack: () => void;
  onClose: () => void;
}

const AddExistingAssetModal: React.FC<AddExistingAssetModalProps> = ({
  chargeId,
  onBack,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  
  const { id: caseId} = useParams<{ id: string }>();

  const existingCharge = useAppSelector((state) =>
    selectChargeById(state, chargeId)
  );

  const [selectedAssets, setSelectedAssets] = useState<CreateChargeAssetLinkageFields[]>([]);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const excludeAssetIds = useAppSelector((state) =>
    selectAssetIdsForCharge(state, chargeId)
  );

  const updatedCharge = useMemo<ChargeWithLinkages | undefined>(() => {
    if (!existingCharge) {
      return undefined;
    }
    return {
      ...existingCharge,
      asset_linkages: [
        ...existingCharge.asset_linkages,
        ...selectedAssets.map((asset) => ({
          asset_id: asset.asset_id,
          charge_id: chargeId,
          linkage_id: `temp-${asset.asset_id}`,
        })),
      ],
    };
  }, [existingCharge, selectedAssets, chargeId]);

  const handleSave = async () => {
    try {
      setIsSaving(true);

      await dispatch(
        addAssetLinkages({
          case_id: caseId ?? "",
          charge_id: chargeId,
          request: {
              asset_linkages: selectedAssets.map((asset) => ({
                asset_id: asset.asset_id,
                charge_id: chargeId,
              })),
          },
        })
      ).unwrap();

      showSuccessToast("Existing assets added successfully.");

      onClose();
    } catch (error) {
      showErrorToast("Failed to add existing assets. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  if (showReviewModal) {
    return (
      <ReviewChangesModal
        existingCharge={existingCharge}
        updatedCharge={updatedCharge}
        onBack={() => setShowReviewModal(false)}
        onClose={onClose}
        onConfirm={handleSave}
        isLoading={isSaving}
      />
    );
  }

  return (
    <ManagementModal
      title="Add Existing Asset"
      onClose={onClose}
      onBack={onBack}
      submitLabel="Review Changes"
      onSubmit={() => setShowReviewModal(true)}
      submitEnabled={selectedAssets.length > 0}
      isLoading={isSaving}
    >
      <div className="">
        <AssetSelectionStep
          excludeAssetIds={excludeAssetIds}
          selectedAssetIds={selectedAssets}
          onSelectionChange={setSelectedAssets}
        />
      </div>
    </ManagementModal>
  );
};

export default AddExistingAssetModal;